import { QueryCache, QueryClient } from '@tanstack/react-query';
import { experimental_createQueryPersister } from '@tanstack/react-query-persist-client';
import ms from 'ms';
import { isErrorResponse } from '#/api/api-types';
import { handleErrorNotification, handleTokenError } from '#/api/errors';
import { ALL_SCOPES } from './auth/scopes';
import storage from './storage';

// Set the prefix and cache buster for the persisted queries
export const prefix = 'react-query:';
const buster = import.meta.env.DEV ? 'dev' : __BUILD_ID__;

export const persister = experimental_createQueryPersister({
  prefix,
  buster,
  storage,
  maxAge: ms('5d'),
  filters: {
    predicate: (query) =>
      query.queryKey.some((key: string) =>
        Object.values(ALL_SCOPES).some(
          (scope) => scope === key && scope.startsWith('get'),
        ),
      ),
  },
  // Serialization and deserialization isn't needed for indexedDB
  // https://developer.mozilla.org/en-US/docs/Web/API/IndexedDB_API/Basic_Terminology#key_characteristics
  serialize: (data) => data,
  deserialize: (data) => data,
});

const queryCache = new QueryCache({
  onError: (error, query) => {
    if (isErrorResponse(error)) {
      if (query.meta?.showErrorToast) {
        // shows after retries have been exhausted
        handleErrorNotification(error, query.queryKey.join(''));
      }
      if (query.queryKey.includes('decode-access-token')) {
        handleTokenError(error);
      }
    }
  },
});

function createQueryClient({
  persistQueries = import.meta.env.MODE !== 'test',
}: {
  persistQueries?: boolean;
} = {}): QueryClient {
  return new QueryClient({
    queryCache,
    defaultOptions: {
      queries: {
        structuralSharing: true,
        retry: import.meta.env.MODE === 'production' ? 3 : false,
        retryDelay: 5000,
        gcTime: ms('2m'),
        staleTime: ms('5m'),
        ...(persistQueries ? { persister: persister.persisterFn } : {}),
      },
      mutations: {
        gcTime: 0,
      },
    },
  });
}

const queryClient = createQueryClient();

export default queryClient;
